import { getFunctions, httpsCallable, Functions } from "firebase/functions";
import { getAuth } from "firebase/auth";
import app from "@/config/firebase";

// Same-origin to avoid CORS (Hosting rewrites /api/createCheckoutSession to the function).
const CHECKOUT_HTTP_URL = "/api/createCheckoutSession";

let functionsInstance: Functions | null = null;

const getFunctionsInstance = (): Functions => {
  if (!functionsInstance) {
    functionsInstance = getFunctions(app, "us-central1");
  }
  return functionsInstance;
};

export const functionsService = {
  async analyzeTranscript(transcriptId: string) {
    const analyzeTranscript = httpsCallable(
      getFunctionsInstance(),
      "analyzeTranscriptFunction"
    );
    const result = await analyzeTranscript({ transcriptId });
    return result.data as { success: boolean; analysis: any; cached: boolean };
  },

  async analyzeFiling(filingId: string) {
    const analyzeFiling = httpsCallable(
      getFunctionsInstance(),
      "analyzeFilingFunction"
    );
    const result = await analyzeFiling({ filingId });
    return result.data as { success: boolean; analysis: any; cached: boolean };
  },

  async askCopilot(
    question: string,
    context?: {
      companyId?: string;
      transcriptId?: string;
      transcriptIds?: string[];
      filingId?: string;
      savedAnalyses?: string[];
      /** Section content to use as prompt context (e.g. summary or R&O text). */
      sectionContext?: string;
    }
  ) {
    const copilot = httpsCallable(getFunctionsInstance(), "copilotFunction");
    const result = await copilot({ question, context });
    return result.data as { success: boolean; answer: string };
  },

  async getCompanyQuote(ticker: string) {
    const getQuote = httpsCallable(getFunctionsInstance(), "getCompanyQuote");
    const result = await getQuote({ ticker });
    return result.data as { success: boolean; quote: any };
  },

  async getCompanyProfile(ticker: string) {
    const getProfile = httpsCallable(
      getFunctionsInstance(),
      "getCompanyProfile"
    );
    const result = await getProfile({ ticker });
    return result.data as { success: boolean; profile: any };
  },

  async getFinancialStatements(
    ticker: string,
    statementType: "income" | "balance" | "cashflow",
    limit?: number
  ) {
    const getStatements = httpsCallable(
      getFunctionsInstance(),
      "getFinancialStatements"
    );
    const result = await getStatements({ ticker, statementType, limit });
    return result.data as { success: boolean; statements: any[] };
  },

  async getKeyMetrics(ticker: string, limit?: number) {
    const getMetrics = httpsCallable(getFunctionsInstance(), "getKeyMetrics");
    const result = await getMetrics({ ticker, limit });
    return result.data as { success: boolean; metrics: any[] };
  },

  async getHistoricalPrices(ticker: string, from?: string, to?: string) {
    const getPrices = httpsCallable(
      getFunctionsInstance(),
      "getHistoricalPrices"
    );
    const result = await getPrices({ ticker, from, to });
    return result.data as { success: boolean; prices: any[] };
  },

  async getEarningsCalendar(from?: string, to?: string) {
    const getCalendar = httpsCallable(
      getFunctionsInstance(),
      "getEarningsCalendar"
    );
    const result = await getCalendar({ from, to });
    return result.data as { success: boolean; calendar: any[] };
  },

  async searchCompaniesFMP(query: string) {
    const search = httpsCallable(getFunctionsInstance(), "searchCompaniesFMP");
    const result = await search({ query });
    return result.data as { success: boolean; results: any[] };
  },

  /** On-demand: fetch transcripts for one company when user opens that company's transcript view. Respects 10-day cache. */
  async fetchCompanyTranscripts(ticker: string) {
    const fn = httpsCallable<
      { ticker: string },
      { success: boolean; ticker: string; created: number; skipped: number; notFound: number; errors: number }
    >(getFunctionsInstance(), "fetchCompanyTranscripts");
    const result = await fn({ ticker: ticker.toUpperCase() });
    return result.data;
  },

  async populateEarningsTranscripts(limit?: number) {
    const populate = httpsCallable(getFunctionsInstance(), "populateEarningsTranscripts");
    const result = await populate({ limit });
    return result.data as { 
      success: boolean; 
      message?: string; 
      count?: number;
      fetched?: number;
      created?: number;
      skipped?: number;
      errors?: number;
      companiesProcessed?: number;
      companiesWithNoTranscripts?: number;
    };
  },

  async populateAllCompanyData(limit?: number, skipExisting?: boolean, backgroundMode?: boolean) {
    const populate = httpsCallable(getFunctionsInstance(), "populateAllCompanyData");
    const result = await populate({ limit, skipExisting, backgroundMode });
    return result.data as { 
      success: boolean;
      message?: string;
      jobId?: string;
      backgroundMode?: boolean;
      results?: {
        transcripts: { created: number; skipped: number; errors: number };
        filings: { created: number; skipped: number; errors: number };
        pressReleases: { created: number; skipped: number; errors: number };
        news: { created: number; skipped: number; errors: number };
      };
    };
  },

  async listUsersForAdmin() {
    const fn = httpsCallable(getFunctionsInstance(), "listUsersForAdmin");
    const result = await fn({});
    return result.data as { success: boolean; users: Array<{ uid: string; email: string; displayName: string | null; tier: string; balance: number }> };
  },

  async adminAddCredits(userId: string, amount: number) {
    const fn = httpsCallable(getFunctionsInstance(), "adminAddCredits");
    await fn({ userId, amount });
  },

  async adminUpdateUserTier(userId: string, tier: string) {
    const fn = httpsCallable(getFunctionsInstance(), "adminUpdateUserTier");
    await fn({ userId, tier });
  },

  async createCheckoutSession(planId: string, successUrl?: string, cancelUrl?: string) {
    const auth = getAuth(app);
    const user = auth.currentUser;
    if (!user) throw new Error("You must be signed in to upgrade.");
    const token = await user.getIdToken();
    const res = await fetch(CHECKOUT_HTTP_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ planId, successUrl, cancelUrl }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg =
        data?.error ||
        res.statusText ||
        (res.status === 503 ? "Payments are not configured. Add your Stripe secret key in Firebase (see STRIPE_SETUP.md)." : "Checkout failed.");
      const e = new Error(msg) as Error & { code?: string };
      e.code = res.status === 401 ? "unauthenticated" : res.status === 503 ? "failed-precondition" : "internal";
      throw e;
    }
    return data as { url: string; sessionId: string };
  },

  async getApiKeyStatus() {
    const fn = httpsCallable(getFunctionsInstance(), "getApiKeyStatus");
    const result = await fn({});
    return result.data as {
      success: boolean;
      keys: {
        openai: { masked: string | null; source: 'firestore' | 'env' | 'not_set' };
        fmp: { masked: string | null; source: 'firestore' | 'env' | 'not_set' };
        apiNinjas: { masked: string | null; source: 'firestore' | 'env' | 'not_set' };
        alphaVantage: { masked: string | null; source: 'firestore' | 'env' | 'not_set' };
      };
    };
  },

  async updateApiKeys(keys: Record<string, string>) {
    const fn = httpsCallable(getFunctionsInstance(), "updateApiKeys");
    const result = await fn(keys);
    return result.data as { success: boolean; updated: string[] };
  },

  async getAboutContent() {
    const fn = httpsCallable(getFunctionsInstance(), "getAboutContent");
    const result = await fn({});
    return result.data as { success: boolean; content: string; updatedAt: any };
  },

  async updateAboutContent(content: string) {
    const fn = httpsCallable(getFunctionsInstance(), "updateAboutContent");
    const result = await fn({ content });
    return result.data as { success: boolean };
  },

  /** Admin: replace placeholder transcript records with real content (SEC / API Ninjas / FMP). */
  async replacePlaceholderTranscripts(limit?: number) {
    const fn = httpsCallable<
      { limit?: number },
      { success: boolean; replaced?: number; notFound?: number; errors?: number; processed?: number; message?: string }
    >(getFunctionsInstance(), "replacePlaceholderTranscriptsCallable");
    const result = await fn({ limit });
    return result.data;
  },

};
